import deepmerge from 'deepmerge';

import type { ThemeColors } from '../../tokens/colors/types';
import { components } from '../../tokens/components/bluestone/ED';
import { createTheme } from '../../tokens/createTheme';
import { shadows } from '../../tokens/shadows/bluestone';
import breakpoints, { mediaQueries, screenMediaQueries } from '../breakpoints';
import type {
  BaseDeprecatedColors,
  DeprecatedBrandColors,
  DeprecatedSemanticColors,
  DeprecatedTheme,
} from '../types';
import baseColors from './colors/base/ED';
import accordionItemTokens from './components/accordionItem.tokens';
import alertTokens from './components/alert.tokens';
import boxTokens from './components/box.tokens';
import createCalendarTokens from './components/calendar.tokens';
import cardTokens from './components/card.tokens';
import checkboxTokens from './components/checkbox.tokens';
import decorationLineTokens from './components/decorationLine.tokens';
import drawerHeaderTokens from './components/drawerHeader.tokens';
import drawerHeaderContentTokens from './components/drawerHeaderContent.tokens';
import highlightedHeaderTokens from './components/highlightedHeader.tokens';
import inputTokens from './components/input.tokens';
import pillTokens from './components/pill.tokens';
import createPopoverTokens from './components/popover.tokens';
import sliderTokens from './components/slider.tokens';
import createTableTokens from './components/table.tokens';
import createTextWithIconTokens from './components/textWithIcon.tokens';
import { space as baseSpace } from './spaces/base';
import { deprecatedSpace } from './spaces/deprecated';

const colorsBrand = {
  0: '#E8ECFB', // brandPrimary.0 - primary.30
  1: '#1E44D5', // brandPrimary.1 - primary.50
  2: '#003399', // brandPrimary.2 - primary.70
  3: '#002C52', // brandPrimary.3 - primary.80
};

const colorGreyLight = '#F2F2F2';
const colorsGrey = {
  0: '#272727', // neutral.100 - neutrals.0
  1: '#4C4C4C', // neutral.90 - neutrals.1
  2: '#6F6F6F', // neutral.80 - neutrals.2
  3: '#909090', // neutral.70 - neutrals.3
  4: '#B2B2B2', // neutral.60 - neutrals.4
  5: '#CBCBCB', // neutral.50 - neutrals.5
  6: '#E6E6E6', // neutral.40 - neutrals.6
  7: colorGreyLight, // neutral.30 - neutrals.7
  8: '#F5F5F5', // neutral.10 - neutrals.8
  light: colorGreyLight, // neutral.30 - neutrals.light
  default: '#888',
};

const colorsBlue = [
  '#D4EAF7',
  '#3398d9',
  '#1E44D5',
  '#003399',
  '#002a4f',
  '#000e2a',
  '#366080',
];

const colorsGreen = { 0: '#1CB670', 1: '#3D8F5D', light: '#E6F5E6' };

const colorsOrange = { 0: '#FF9600', 1: '#DC5F0B', light: '#FFEACC' };

const colorsRed = [
  '#FDE7EA', // critical.light - critical.30
  '#F01C3D', // critical.default - critical.50
  '#9B0000', // critical.dark - critical.70
];

const brandSecondary = '#FFCC00';
const rating = '#FFCC00';
const colors: ThemeColors &
  BaseDeprecatedColors &
  DeprecatedSemanticColors &
  DeprecatedBrandColors = deepmerge(
  {
    brandPrimary: colorsBrand,
    brandSecondary,
    neutrals: colorsGrey,
    positive: {
      light: colorsGreen.light,
      default: colorsGreen[0],
      dark: colorsGreen[1],
    },
    informative: {
      light: colorsBlue[0],
      default: colorsBlue[1],
      dark: colorsBlue[2],
      darkest: colorsBlue[6],
    },
    warning: {
      light: colorsOrange.light,
      default: colorsOrange[0],
      dark: colorsOrange[1],
    },
    negative: {
      light: colorsRed[0],
      default: colorsRed[1],
      dark: colorsRed[2],
    },
    critical: {
      light: colorsRed[0],
      default: colorsRed[1],
      dark: colorsRed[2],
    },
    rating: rating,
    black: '#000',
    white: '#fff',
    trainProductColor: '#5335B6',
    flightProductColor: '#4F8EFF',
    promotional: {
      primary: {
        default: colorsBrand[3],
        dark: '#000D29',
      },
      secondary: {
        default: '#FFD633',
        dark: '#8F7200',
      },
    },
  },
  baseColors
);

const sizes = [16, 24, 32, 64, 128, 256];

const space = {
  ...baseSpace,
  ...deprecatedSpace,
};

const lineHeights = {
  body: 1.5,
  bodies: {
    base: '24px',
    small: '21px',
    xSmall: '18px',
  },
  heading: 1.25,
  headings: {
    1: '38px',
    2: '30px',
    3: '24px',
    4: '22px',
    5: '20px',
    6: '18px',
  },
  display: {
    1: '48px',
    2: '42px',
  },
  overline: '12px',
};

const decorationLine = decorationLineTokens({
  brandSecondary,
  colorsGrey,
  colors,
});
const popover = createPopoverTokens({});

const radii = {
  small: '4px',
  regular: '8px',
  big: '16px',
  full: '9999px',
};

const fontSizeBody = [12, 14, 16, 18];
const fontSizes = {
  body: fontSizeBody,
  bodies: {
    base: 16,
    small: 14,
    xSmall: 12,
    xxSmall: 10,
  },
  heading: [20, 24, 32],
  headings: {
    1: 32,
    2: 24,
    3: 20,
    4: 18,
    5: 16,
    6: 14,
  },
  display: {
    1: 44,
    2: 40,
  },
  overline: 12,
};
const fontWeights = {
  normal: 400,
  medium: 500,
  bold: 600,
};
const borders = [0, '1px solid', '2px solid', '4px solid'];
const borderWidths = [0, 1, 2, 4, 8, 12];
const iconSizes = {
  tiny: 12,
  small: 16,
  base: 20,
  medium: 24,
  large: 32,
  xlarge: 64,
};

const drawerHeader = drawerHeaderTokens({
  colorsBrand,
  colors,
  borders,
  space,
});

const drawerHeaderContent = drawerHeaderContentTokens({ borders, colors });

const box = boxTokens({ colors });

const card = cardTokens({ radii });

const highlightedHeader = highlightedHeaderTokens(fontSizes);

const pill = pillTokens({ colors, colorsGrey });

const alert = alertTokens({ colors, borders });

const slider = sliderTokens({
  colors,
  colorsBrand,
  sizes,
  space,
  fontSizeBody,
});

const checkbox = checkboxTokens({
  borders,
  colors,
  colorsBrand,
  space,
  lineHeights,
  fontSizeBody,
});

const accordionItem = accordionItemTokens({
  borders,
  colors,
  space,
  fontSizeBody,
  fontWeights,
  iconSizes,
});

const textWithIcon = createTextWithIconTokens({ colors, fontSizes, iconSizes });
const table = createTableTokens({ borderWidths });
const input = inputTokens({ fontSizes, colors });
const calendar = createCalendarTokens({ colors });

export const ED = {
  colors: {
    ...colors,
    text: colors,
    drawerHeader: drawerHeader.colors,
    box: box.colors,
    decorationLine: decorationLine.colors,
    drawerHeaderContent: drawerHeaderContent.colors,
    pill: pill.colors,
    alert: alert.colors,
    slider: slider.colors,
    checkbox: checkbox.colors,
    brandedProgressBar: brandSecondary,
    textWithIcon: textWithIcon.color,
    accordionItem: accordionItem.colors,
    input: input.colors,
    calendar: calendar.colors,
  },
  sizes: {
    ...sizes,
    decorationLine: decorationLine.sizes,
    slider: slider.sizes,
    checkbox: checkbox.sizes,
    drawerHeader: drawerHeader.sizes,
    input: input.sizes,
    popover: popover.sizes,
  },
  shadows,
  breakpoints,
  mediaQueries,
  screenMediaQueries,
  space: {
    ...space,
    drawerHeader: drawerHeader.space,
    decorationLine: decorationLine.space,
    slider: slider.space,
    checkbox: checkbox.space,
    accordionItem: accordionItem.space,
    popover: popover.space,
    input: input.space,
  },
  fonts: {
    body: "'Rubik', -apple-system, '.SFNSText-Regular', 'San Francisco', 'Segoe UI', 'Helvetica Neue', 'Lucida Grande', sans-serif",
  },
  fontSizes: {
    ...fontSizes,
    slider: slider.fontSizes,
    checkbox: checkbox.fontSizes,
    textWithIcon: textWithIcon.fontSize,
    accordionItem: accordionItem.fontSizes,
    highlightedHeader: highlightedHeader.fontSizes,
    input: input.fontSizes,
  },
  fontWeights: {
    ...fontWeights,
    button: {
      default: fontWeights.medium,
    },
    accordionItem: accordionItem.fontWeights,
  },
  lineHeights: {
    ...lineHeights,
    checkbox: checkbox.lineHeight,
    highlightedHeader: highlightedHeader.lineHeights,
    input: input.lineHeight,
  },
  letterSpacings: {
    normal: 'normal',
    tracked: '0.1em',
    tight: '-0.05em',
    mega: '0.25em',
    extended: '0.03em',
    condensed: '-0.03em',
    bitCondensed: '-0.02em',
  },
  borders: {
    ...borders,
    drawerHeader: drawerHeader.borders,
    drawerHeaderContent: drawerHeaderContent.borders,
    checkbox: checkbox.borders,
    accordionItem: accordionItem.borders,
    alert: alert.borders,
  },
  borderWidths: {
    ...borderWidths,
    table: table.borderWidths,
  },
  borderStyles: {
    table: table.borderStyles,
  },
  radii: {
    ...radii,
    0: 0,
    1: 4,
    2: 8,
    3: 12,
    4: 16,
    5: '50%',
    6: '100%',
    7: '9999px',
    cardRadii: card.sizesRadii,
  },
  iconSizes: {
    ...iconSizes,
    textWithIcon: textWithIcon.iconSize,
    accordionItem: accordionItem.iconSizes,
  },
  inputWidths: {
    full: '100%',
    xxxl: 380,
    xxl: 300,
    xl: 270,
    l: 180,
    m: 100,
    s: 65,
    xs: 50,
  },
  inputHeights: {
    sm: 30,
    m: 40,
    l: 56,
  },
  zIndices: {
    popup: 100,
    tooltip: 200,
    popover: 300,
    stickyBox: 400,
    modal: 500,
  },
  transition: {
    slider: slider.transition,
  },
  opacity: {
    checkbox: checkbox.opacity,
  },
};

const theme: DeprecatedTheme = createTheme('blueStone', ED, components);

export default theme;
